import {
  Component,
  type ComponentType,
  type ReactNode,
} from 'react';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  type DragEndEvent,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { CAPTURE_HEIGHT, CAPTURE_WIDTH, useGalleryStore } from './galleryStore';
import { galleryScreens } from './screens';

const initials = (label: string) =>
  label
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');

type ThumbBoundaryProps = {
  label: string;
  children: ReactNode;
};

type ThumbBoundaryState = { hasError: boolean };

class ThumbBoundary extends Component<ThumbBoundaryProps, ThumbBoundaryState> {
  state: ThumbBoundaryState = { hasError: false };

  static getDerivedStateFromError(): ThumbBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error) {
    // Keep the dock usable if a screen fails to preview.
    console.warn('[SequenceList] thumb preview failed:', error.message);
  }

  render() {
    if (this.state.hasError) {
      return (
        <span className="gallery-sequence-thumb-fallback">
          {initials(this.props.label)}
        </span>
      );
    }
    return this.props.children;
  }
}

type LiveThumbProps = {
  screenId: string;
  label: string;
  route: string;
  Component: ComponentType;
};

const LiveThumb = ({ screenId, label, Component }: LiveThumbProps) => (
  <div className="gallery-sequence-thumb" aria-hidden="true">
    {/* key remounts the boundary after a prior failure (e.g. nested-router crash). */}
    <ThumbBoundary key={screenId} label={label}>
      <div className="gallery-sequence-thumb-live">
        {/* Reuse the app BrowserRouter — nesting MemoryRouter crashes RR v7. */}
        <div
          className="gallery-sequence-thumb-stage"
          style={{ width: CAPTURE_WIDTH, height: CAPTURE_HEIGHT }}
        >
          <Component />
        </div>
      </div>
    </ThumbBoundary>
  </div>
);

type SequenceItemProps = {
  id: string;
};

const SequenceItem = ({ id }: SequenceItemProps) => {
  const screen = galleryScreens.find((entry) => entry.id === id);
  const removeFromSequence = useGalleryStore((s) => s.removeFromSequence);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  if (!screen) return null;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`gallery-sequence-item ${isDragging ? 'is-dragging' : ''}`}
    >
      <button
        type="button"
        className="gallery-sequence-handle"
        aria-label={`Drag to reorder ${screen.label}`}
        {...attributes}
        {...listeners}
      >
        ⋮⋮
      </button>
      <LiveThumb
        screenId={screen.id}
        label={screen.label}
        route={screen.route}
        Component={screen.Component}
      />
      <div className="gallery-sequence-meta">
        <span className="gallery-sequence-label">{screen.label}</span>
        <span className="gallery-sequence-route">{screen.route}</span>
      </div>
      <button
        type="button"
        className="gallery-sequence-remove"
        aria-label={`Remove ${screen.label} from sequence`}
        onClick={() => removeFromSequence(id)}
      >
        ✕
      </button>
    </div>
  );
};

export const SequenceList = () => {
  const sequence = useGalleryStore((s) => s.sequence);
  const reorderSequence = useGalleryStore((s) => s.reorderSequence);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const onDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = sequence.indexOf(String(active.id));
    const newIndex = sequence.indexOf(String(over.id));
    if (oldIndex < 0 || newIndex < 0) return;
    reorderSequence(arrayMove(sequence, oldIndex, newIndex));
  };

  if (sequence.length === 0) {
    return (
      <p className="gallery-sequence-empty">
        Add screens with + to build an export sequence.
      </p>
    );
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={onDragEnd}
    >
      <SortableContext items={sequence} strategy={verticalListSortingStrategy}>
        <div className="gallery-sequence-list">
          {sequence.map((id) => (
            <SequenceItem key={id} id={id} />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
};
